import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const API_URL = 'https://iia-backend-production.up.railway.app';

const AdminPathManager = () => {
  const { pathId } = useParams();
  const navigate = useNavigate();

  const [path, setPath] = useState(null);
  const [assignedCourses, setAssignedCourses] = useState([]);
  const [allCourses, setAllCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, [pathId]);

  const fetchData = async () => {
    try {
      const [pathsRes, assignedRes, allCoursesRes] = await Promise.all([
        axios.get(`${API_URL}/api/instructional/paths/all`),
        axios.get(`${API_URL}/api/instructional/paths/${pathId}/courses/all`),
        axios.get(`${API_URL}/api/instructional/courses/all`)
      ]);
      const currentPath = pathsRes.data.paths.find(
        p => p.id === parseInt(pathId)
      );
      setPath(currentPath);
      setAssignedCourses(assignedRes.data.courses);
      setAllCourses(allCoursesRes.data.courses);
    } catch (err) {
      console.error('Fetch error:', err);
    }
  };

  const handleAssignCourse = async () => {
    if (!selectedCourse) return;
    try {
      await axios.post(
        `${API_URL}/api/instructional/paths/${pathId}/courses`,
        { 
          course_id: selectedCourse,
          order_index: assignedCourses.length + 1
        }
      );
      setMessage('Course assigned successfully');
      setSelectedCourse('');
      fetchData();
    } catch (err) {
      setMessage('Error assigning course');
    }
  };

  const handleRemoveCourse = async (courseId) => {
    try {
      await axios.delete(
        `${API_URL}/api/instructional/paths/${pathId}/courses/${courseId}`
      );
      setMessage('Course removed');
      fetchData();
    } catch (err) {
      setMessage('Error removing course');
    }
  };

  // Filter out already assigned courses
  const unassignedCourses = allCourses.filter(
    c => !assignedCourses.find(ac => ac.id === c.id)
  );

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Admin Panel</span>
        </div>
        <button 
          style={styles.returnBtn} 
          onClick={() => navigate('/admin')}
        >
          ← Back to Admin Panel
        </button>
      </div>

      <div style={styles.main}>
        <h2 style={styles.pageTitle}>
          📚 Managing Path: {path?.title}
        </h2>
        <p style={styles.pageSubtitle}>{path?.description}</p>

        {message && (
          <div style={styles.message}>{message}</div>
        )}

        {/* Assign Course */}
        <div style={styles.formCard}>
          <h3 style={styles.formTitle}>Assign Course to Path</h3>
          {unassignedCourses.length === 0 ? (
            <p style={styles.emptyText}>
              All courses are already assigned to this path
            </p>
          ) : (
            <div style={styles.assignRow}>
              <select
                style={styles.select}
                value={selectedCourse}
                onChange={e => setSelectedCourse(e.target.value)}
              >
                <option value="">Select a course...</option>
                {unassignedCourses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.title}
                  </option>
                ))}
              </select>
              <button 
                style={styles.assignBtn} 
                onClick={handleAssignCourse}
              >
                Assign Course
              </button>
            </div>
          )}
        </div>

        {/* Assigned Courses */}
        <div style={styles.listSection}>
          <h3 style={styles.listTitle}>
            Courses in this Path ({assignedCourses.length})
          </h3>
          {assignedCourses.length === 0 && (
            <p style={styles.emptyText}>No courses assigned yet</p>
          )}
          {assignedCourses.map((course, index) => (
            <div key={course.id} style={styles.listCard}>
              <div style={styles.listCardLeft}>
                <span style={styles.orderBadge}>#{index + 1}</span>
                <div>
                  <h4 style={styles.listCardTitle}>{course.title}</h4>
                  <p style={styles.listCardDesc}>{course.description}</p>
                </div>
              </div>
              <div style={styles.listCardRight}>
                <button
                  style={styles.manageBtn}
                  onClick={() => navigate(`/admin/course/${course.id}`)}
                >
                  Manage →
                </button>
                <button
                  style={styles.removeBtn}
                  onClick={() => handleRemoveCourse(course.id)}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1A2744',
    color: '#ffffff',
  },
  header: {
    backgroundColor: '#0f1a33',
    borderBottom: '1px solid #C49A28',
    padding: '16px 32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  logo: {
    color: '#C49A28',
    fontSize: '24px',
    fontWeight: '800',
    margin: '0',
    letterSpacing: '2px',
  },
  logoText: {
    color: '#ffffff80',
    fontSize: '14px',
    letterSpacing: '1px',
  },
  returnBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #C49A28',
    color: '#C49A28',
    padding: '8px 16px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
  },
  main: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '32px',
  },
  pageTitle: {
    color: '#C49A28',
    fontSize: '28px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  pageSubtitle: {
    color: '#ffffff60',
    fontSize: '14px',
    margin: '0 0 32px 0',
  },
  message: {
    backgroundColor: '#1E8449',
    color: '#ffffff',
    padding: '12px 16px',
    borderRadius: '6px',
    marginBottom: '24px',
    fontSize: '14px',
  },
  formCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '24px',
    marginBottom: '24px',
  },
  formTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 16px 0',
  },
  assignRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
  },
  select: {
    backgroundColor: '#1A2744',
    border: '1px solid #ffffff20',
    borderRadius: '6px',
    padding: '12px 16px',
    color: '#ffffff',
    fontSize: '14px',
    flex: 1,
    outline: 'none',
  },
  assignBtn: {
    backgroundColor: '#C49A28',
    color: '#1A2744',
    border: 'none',
    borderRadius: '6px',
    padding: '12px 24px',
    fontSize: '14px',
    fontWeight: '700',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  listSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  listTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0',
  },
  emptyText: {
    color: '#ffffff40',
    fontSize: '14px',
  },
  listCard: {
    backgroundColor: '#0f1a33',
    border: '1px solid #ffffff15',
    borderRadius: '10px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '16px',
  },
  listCardLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    flex: 1,
  },
  orderBadge: {
    backgroundColor: '#C49A2820',
    color: '#C49A28',
    padding: '6px 10px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '700',
    whiteSpace: 'nowrap',
  },
  listCardTitle: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  listCardDesc: {
    color: '#ffffff60',
    fontSize: '13px',
    margin: '0',
  },
  listCardRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  manageBtn: {
    backgroundColor: '#1A6B8A',
    color: '#ffffff',
    border: 'none',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '600',
  },
  removeBtn: {
    backgroundColor: 'transparent',
    border: '1px solid #ff000040',
    color: '#ff6666',
    padding: '6px 14px',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '12px',
  },
};

export default AdminPathManager;